import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import trackedElementsData from './trackElements.json';
import { postTrackLog } from './api';

/**
 * Tracks events on the specified element and its children by given selector.
 *
 * How doesn't it work:
 * 1. Fetch `trackElements.json`, find those elements to track by property `selector`.
 * 2. Attach specific event listeners to the those elements by property `events`.
 * 3. When the event is triggered, look for element that need to return to backend by property `returnSelector`.
 *    If there is no `returnSelector`, return the element that triggered the event,
 *    otherwise find the closest ancestor or descendant of the element that matches the `returnSelector`.
 *    The reason for this is that the element that triggered the event may not be the element that needs to be returned, for example:
 *    A svg icon is clicked, the triggered element might be the <path> tag, but the element that needs to be returned is the parent element of the image, a <span> tag.
 *
 * TODO:
 * 1. Optimize the performance of the querySelector and querySelectorAll methods.
 * 2. Ensure all process is occurs in the client side.
 *
 * @param [props] {Object}
 * @param props.getSessionId {Function} A function that returns the session id.
 * @param props.sessionId {string}
 */
const useEventTracker = (props) => {
  const handledInitEventTrack = useRef(false);

  /**
   * Returns all elements matching the given selector, including the element itself if it matches.
   * @param element {HTMLElement} The element to start searching from.
   * @param selector {string} The selector to match.
   * @returns {HTMLElement[] || NodeListOf<Element>}
   */
  const queryChildrenBySelectorIncludingSelf = (element, selector) => {
    // Check if the element itself matches the selector
    const matchesSelector = element.matches(selector);

    // Get all matching descendants
    const descendantMatches = element.querySelectorAll(selector);

    // If the element itself matches, include it in the results
    if (matchesSelector) {
      return [element, ...descendantMatches];
    } else {
      return descendantMatches;
    }
  }

  const queryClosestAncestorBySelectorIncludingSelf = (element, selector) => {
    // Check if the element itself matches the selector
    const matchesSelector = element.matches(selector);

    if (matchesSelector) {
      return element;
    }

    const ancestralMatch = element.closest(selector);
    if(ancestralMatch) {
      return ancestralMatch;
    } else {
      return null;
    }
  }

  const queryClosestDescendantBySelectorIncludingSelf = (element, selector) => {
    // Check if the element itself matches the selector
    const matchesSelector = element.matches(selector);

    if (matchesSelector) {
      return element;
    }

    // Get all matching ancestors and descendants
    const descendantMatch = element.querySelector(selector);
    if(descendantMatch) {
      return element;
    } else {
      return null;
    }
  }

  const sendTrackLog = useCallback(
    (data) => {
      if(props && props.getSessionId && props.sessionId) {
          return postTrackLog(data, [{
              key: 'Legallink-Session', value: props.getSessionId
          }]);
      } else {
          return postTrackLog(data);
      }
    },
    [props],
  );

  /**
   * Attaches event listeners to the specified element and its children by given selector.
   * @param root {HTMLElement} The root element to attach event listeners to.
   */
  const attachEventListeners = (root) => {

    if(!root || !root.querySelectorAll) {
      return;
    }

    trackedElementsData.trackedElements.forEach(({
       selector,
       events,
       /**@type {string | undefined}
        * If not provided, the element that triggered the event will be returned.
        * It could be any element nested in that element matches to selector.
        * For example:
        * ```html```
        * <div class="css-selector">
        *     <svg class="css-selector-svg">
        *         <path></path>
        *     </svg>
        * </div>
        * ```
        * The svg icon is clicked, with selector '.css-selector', the triggered element might be the <path> tag, but the element that needs to be returned is the parent element of the image, a <div> tag.
        * So that we need to set returnSelector to '.css-selector', otherwise it returns the <path> tag.
        * If we want to return the <svg> tag, we can set returnSelector to '.css-selector-svg'.
        */
       returnSelector
     }) => {
      const elements = queryChildrenBySelectorIncludingSelf(root, selector);
      elements.forEach(element => {
        events.forEach(eventType => {
          // Check if the event listener is already attached
          if (element.getAttribute(`data-event-attached-${eventType}`) !== 'true') {
            element.setAttribute(`data-event-attached-${eventType}`, 'true');
            element.addEventListener(eventType, (event) => {
              const element = returnSelector
                ? queryClosestAncestorBySelectorIncludingSelf(event.target, returnSelector) || queryClosestDescendantBySelectorIncludingSelf(event.target, returnSelector)
                : event.target;
              const eventData = {
                Url: window.location.href,
                Event: eventType,
                Element: element ? element.outerHTML : '',
                // Selector: selector,
              };
              sendTrackLog(eventData)
                .then((response) => {
                  console.log('DEBUG >> sendTrackLog response:', response);
                })
                .catch((error) => {
                  console.error('DEBUG >> sendTrackLog error:', error);
                });
              console.log('DEBUG >> event', event, 'eventData:', eventData);
            });
          }
        });
      });
    });
  }

  // useLayoutEffect(() => {
  //   postTrackLog({}, [{key: 'Legallink-Session', value: getSessionId}])
  // }, []);

  useEffect(() => {

    // Function to handle window load event
    const handleWindowLoad = () => {
      // Trigger sendTrackLog when window is loaded
      const eventData = {
        Url: window.location.href,
        Event: 'onload',
        Element: '',
      }
      sendTrackLog(eventData)
        .then((response) => {
          console.log('DEBUG >> sendTrackLog response:', response);
        })
        .catch((error) => {
          console.error('DEBUG >> sendTrackLog error:', error);
        });
      console.log('DEBUG >> event', 'onload', 'eventData:', eventData);
    };

    if(!handledInitEventTrack.current) {
      if(trackedElementsData && trackedElementsData.tackWindowLoad) {
        handleWindowLoad();
      }
      handledInitEventTrack.current = true;
    }

    attachEventListeners(document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            // Do something with the added nodes
            attachEventListeners(node);
          });
        }
      });
    });

    const targetNode = document.body;
    const config = { childList: true, subtree: true };

    setTimeout(() => {
      observer.observe(targetNode, config);
    }, 0);

    return () => {
      observer.disconnect();
    }
  }, []);

  // useEffect(() => {
  //   // Attach event listeners after a slight delay to ensure DOM is fully loaded
  //   setTimeout(attachEventListeners, 3000);
  // }, []);
};

export default useEventTracker;
